import CircularProgress from "@mui/material/CircularProgress";
import { useWebSocketContext } from "apollo/client/WebSocketContext";
import { useLazyGetAuction } from "apollo/hooks/queries";
import { useOnCandidatesCalculated } from "apollo/hooks/subscriptions";
import {
  CANDIDATES_STATUS_PENDING,
  CANDIDATES_STATUS_SUCCESS,
  TRACK_EVENTS,
} from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { isRehabSearch } from "core/model/auctions";
import { formatMedicalSuppliesLocation } from "core/model/utils/location/formatters";
import { ONTOLOGIES, OntologyType } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { Auction, GetOntologyType } from "core/types";
import { getOrderedItems } from "ds_legacy/components/ChipGroup";
import RSButton from "ds_legacy/components/RSButton";
import { CUSTOM_BLACK, GREY_600 } from "ds_legacy/materials/colors";
import { dp, margin } from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_16,
  FONT_WEIGHT_REGULAR,
  Heading,
  LINE_HEIGHT_20,
} from "ds_legacy/materials/typography";
import { usePrint } from "dsl/atoms/Contexts";
import { useCandidates } from "dsl/atoms/SearchCandidates";
import { solutionSortOrder } from "dsl/ecosystems/PatientCreatePage/PatientCreateForm";
import { PatientProfilePrint } from "dsl/ecosystems/PatientProfilePrint/PatientProfilePrint";
import { useRefreshComponent } from "dsl/hooks";
import { useCareseekerNavigationHandlers } from "dsl/hooks/useNavigationHandlers";
import PatientNavigation from "dsl/organisms/PatientNavigation";
import { PatientProfileDetailsCareseeker } from "dsl/organisms/PatientProfileDetailsCareseeker";
import { formatTimestamp } from "dsl/static/utils";
import { useEffect } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import Translations from "translations/types";
import PatientProfileStatusBanner from "./PatientProfileStatusBanner";

const CTA_WIDTH = 298;

export const getSelectedOntologies = (
  values: number[] | undefined,
  type: OntologyType,
  getOntology: GetOntologyType,
  sortOrder?: Record<number, number>,
) => {
  if (!values?.length) return;

  // Convert number[] to objects with value property for getOrderedItems
  const itemsWithValue = values.map((value) => ({ value }));

  const sortedItems = sortOrder
    ? getOrderedItems(sortOrder, itemsWithValue)
    : itemsWithValue;

  return sortedItems
    .map(({ value }) => getOntology({ key: value, type }))
    .join(", ");
};

export const useCandidatesSynchronizer = ({
  auction,
}: {
  auction: Auction;
}) => {
  const getAuction = useLazyGetAuction();

  const skip =
    !auction.assessment_completed ||
    auction.candidates_status === CANDIDATES_STATUS_SUCCESS;

  const { isAlive } = useWebSocketContext();

  // subscribes to websocket, updates auction when candidates are calculated
  useOnCandidatesCalculated(auction.id, { skip });

  const timeTillRefetch = isAlive !== false ? 5 * 1000 : 2 * 1000;

  // if response not received in {timeTillRefetch} seconds, fetch auction from DB
  const refetchCount = useRefreshComponent(skip ? null : timeTillRefetch);

  useEffect(() => {
    if (!refetchCount || skip) return;

    console.warn(`refetched auction ${auction.id} for candidates status`, {
      isAlive,
      refetchCount,
    });

    getAuction(auction.id);
  }, [skip, refetchCount]);
};

const getSearchRadiusTranslation = ({
  auction,
  translations,
}: {
  auction: Auction;
  translations: Translations;
}): string | undefined => {
  if (auction?.country_wide_search) {
    return translations.patient.countrySearch;
  }

  if (auction?.radius_in_meter) {
    return `${auction?.radius_in_meter / 1000} km`;
  }
  return undefined;
};

const TransferDetailRow = ({
  label,
  testId,
  value,
}: {
  label: string;
  testId?: string;
  value: number | string | null | undefined;
}) => {
  if (value == null) {
    return null;
  }

  return (
    <div className="flex">
      <Body
        color={CUSTOM_BLACK}
        data-testid={testId}
        display="inline-block"
        fontSize={FONT_SIZE_16}
        fontWeight={FONT_WEIGHT_REGULAR}
        margin={margin(0)}
      >
        <Body
          color={GREY_600}
          display="inline-block"
          fontSize={FONT_SIZE_16}
          fontWeight={FONT_WEIGHT_REGULAR}
          margin={margin(0, 0.5, 0, 0)}
          style={{ whiteSpace: "nowrap" }}
        >
          {label}
        </Body>
        {value}
      </Body>
    </div>
  );
};

export function TransferDetails({ auction }: { auction: Auction }) {
  const getOntology = useGetOntology();
  const translations = useTranslations();
  const { currentLocale: locale } = useDateLocale();
  const navigationHandlers = useCareseekerNavigationHandlers();
  const potentialReceivers = useCandidates().candidates;
  const { trackEvent } = useTracking();

  const typeOfTransfer = getSelectedOntologies(
    auction.solutions,
    ONTOLOGIES.SOLUTION,
    getOntology,
    solutionSortOrder,
  );
  const specialisation = getSelectedOntologies(
    auction.specializations,
    ONTOLOGIES.SPECIALIZATIONS,
    getOntology,
  );

  const dischargeDate = formatTimestamp(
    auction.patient.profile?.discharge_date,
    locale,
  );
  const startDate = formatTimestamp(auction.start_date, locale);
  const treatmentLocation = formatMedicalSuppliesLocation(auction);
  const searchRadius = getSearchRadiusTranslation({
    auction,
    translations,
  });

  if (
    !typeOfTransfer &&
    !specialisation &&
    !startDate &&
    !treatmentLocation &&
    !searchRadius &&
    !potentialReceivers
  ) {
    return null;
  }

  const candidatesCalculationPending =
    auction?.candidates_status === CANDIDATES_STATUS_PENDING;
  const candidatesCalculationCompleted =
    auction?.candidates_status === CANDIDATES_STATUS_SUCCESS;

  return (
    <div className="w-[359px] flex-col space-y-4 rounded bg-white p-6 shadow-md">
      <Heading color={CUSTOM_BLACK} margin={margin(0, 0, 1, 0)}>
        {translations.patient.transferDetails.title}
      </Heading>
      <TransferDetailRow
        label={translations.patient.tooltipPatientMenu.typeOfTransfer}
        value={typeOfTransfer}
      />
      <TransferDetailRow
        label={translations.patient.tooltipPatientMenu.specialisation}
        value={specialisation}
      />
      <TransferDetailRow
        label={translations.patient.expectedStartDate}
        testId="start_date"
        value={startDate}
      />
      {isRehabSearch(auction.search_type) && (
        <TransferDetailRow
          label={translations.patient.expectedDischargeDate}
          testId="discharge_date"
          value={dischargeDate}
        />
      )}
      <TransferDetailRow
        label={translations.patient.tooltipPatientMenu.treatmentLocation}
        value={treatmentLocation}
      />
      <TransferDetailRow
        label={translations.patient.tooltipPatientMenu.searchRadius}
        value={searchRadius}
      />
      <TransferDetailRow
        label={translations.patient.tooltipPatientMenu.potentialReceivers}
        testId="search-candidates"
        value={potentialReceivers}
      />
      <div className="relative my-2 ml-1 flex items-center justify-center pb-0 pl-1">
        {candidatesCalculationPending && (
          <>
            <div style={{ left: 0, position: "absolute" }}>
              <CircularProgress size={FONT_SIZE_16} />
            </div>
            <Body
              color={GREY_600}
              fontSize={FONT_SIZE_16}
              fontStyle="italic"
              fontWeight={FONT_WEIGHT_REGULAR}
              lineHeight={LINE_HEIGHT_20}
              margin={margin(0, 0, 0, 3)}
            >
              {translations.search.candidatesCalculating}
            </Body>
          </>
        )}
      </div>
      <RSButton
        color="secondary"
        disabled={!candidatesCalculationCompleted}
        id="go_to_search_merge"
        loading="na"
        style={{
          boxSizing: "border-box",
          margin: margin(1, 0, 0, 0),
          minWidth: dp(CTA_WIDTH),
          width: dp(CTA_WIDTH),
        }}
        variant="contained"
        onClick={() => {
          trackEvent({ name: TRACK_EVENTS.GO_TO_SEARCH_BUTTON_CLICKED });
          navigationHandlers.patient.goToSearch({
            auctionId: auction.id,
            patientId: auction.patient.id,
          });
        }}
      >
        {translations.search.setUpSearch}
      </RSButton>
    </div>
  );
}

export const PatientProfilePresenter = ({ auction }: { auction: Auction }) => {
  if (!auction) return null;

  return (
    <div className="flex h-full min-h-[60vh] w-full justify-center gap-6 overflow-visible bg-gray-50 p-6">
      <PatientProfileDetailsCareseeker auction={auction} />
      <div className="h-full overflow-visible">
        <div className="sticky left-0 top-[220px] w-[359px]">
          <PatientProfileStatusBanner auction={auction} />
          <TransferDetails auction={auction} />
        </div>
      </div>
    </div>
  );
};

const PatientProfile = ({ auction }: { auction: Auction }) => {
  useCandidatesSynchronizer({ auction });
  const print = usePrint();

  if (!auction) return null;

  if (print) return <PatientProfilePrint auction={auction} forClinic />;

  return (
    <PatientNavigation auction={auction} patient={auction.patient}>
      <PatientProfilePresenter auction={auction} />
    </PatientNavigation>
  );
};

export default PatientProfile;
